import {
  ActionIcon,
  Avatar,
  Button,
  Card,
  Group,
  Indicator,
  Menu,
  Modal,
  Select,
  Text,
  UnstyledButton,
  rem,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import fetchApi from "../Lib/api";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconDoor,
  IconTrash,
  IconUsersGroup,
  IconX,
} from "@tabler/icons-react";
import Notification from "../Lib/Notification";
import useStartupStore from "../../stores/startupStore";
import { useTranslation } from "react-i18next";
import useUserStore from "../../stores/userStore";
import Mixpanel from "../Lib/mixpanel";

export default function ManageRequestToJoinTeam() {
  const [opened, { open, close }] = useDisclosure(false);
  const [users, setUsers] = useState({
    pending_requests: [],
    not_pending_requests: [],
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [removeMemberModal, setRemoveMemberModal] = useState({
    opened: false,
    user: null,
    index: undefined,
  });
  const { t } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("manage_request")) fetchData();
  }, [open, close]);

  const {
    isOwner,
    isMember,
    slug,
    entity,
    members,
    role,
    setMembers,
    removeMembers,
    pendingJoinRequest,
  } = useStartupStore((state) => ({
    slug: state.slug,
    isOwner: state.isOwner,
    isMember: state.isMember,
    members: state.members,
    role: state.role,
    setMembers: state.setMembers,
    removeMembers: state.removeMembers,
    entity: state.entity,
    pendingJoinRequest: state.pendingJoinRequest,
  }));

  const {
    user: { id },
  } = useUserStore((state) => state.user);

  const fetchData = async () => {
    Mixpanel.custom('Manage Team Button Clicked', { startup_slug: slug });
    setLoading(true);
    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/join_requests/pending_requests_for_startup`,
        "GET"
      );
      if (response.ok) {
        const res = await response.json();
        console.log(res);
        setUsers(res);
        open();
      } else {
        const msg = (await response.json()).error;
        console.error(msg);
      }
    } catch (error) {
      console.error("Error during password change:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (accepted, index) => {
    const startTime = performance.now();
    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/join_requests/${
          users.pending_requests[index].join_request_id
        }/${accepted ? "accept" : "reject"}_request`,
        "PATCH"
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (response.ok) {
        const res = await response.json();
        setNotification({ type: "success", content: res.message });
        if (accepted) {
          setMembers(res.user);
          setUsers((u) => ({
            not_pending_requests: [
              ...u.not_pending_requests,
              u.pending_requests[index],
            ],
            pending_requests: u.pending_requests.filter((_, i) => i !== index),
          }));

          Mixpanel.content('Join Request', 'Accepted', {
            accepted_user_id: requestedUser.id,
            response_time_ms: responseTime,
            startup_slug: slug
          });
        } else {
          setUsers((u) => ({
            ...u,
            pending_requests: u.pending_requests.filter((_, i) => i !== index),
          }));
          Mixpanel.content('Join Request', 'Rejected', {
            rejected_user_id: requestedUser.id,
            response_time_ms: responseTime,
            startup_slug: slug
          });
        }
      } else {
        const msg = (await response.json()).error;
        setNotification({ type: "failure", content: msg });
        Mixpanel.error('Join Request Action Error', {
          error_message: msg,
          error_type: 'API Error',
          http_status: response.status,
          component: 'ManageRequestToJoinTeam',
          user_action: `${accepted ? 'Accept' : 'Reject'} Join Request`,
          requested_user_id: requestedUser.id,
          response_time_ms: responseTime,
          startup_slug: slug
        });
        console.error(msg);
      }
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      Mixpanel.error('Join Request Action Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'ManageRequestToJoinTeam',
        user_action: `${accepted ? 'Accept' : 'Reject'} Join Request`,
        requested_user_id: requestedUser.id,
        response_time_ms: responseTime,
        startup_slug: slug
      });
      console.error("Error during password change:", error);
    }
  };

  const handleRole = async (membershipId, newRoleIndex, userToUpdate) => {
    const memberToUpdate = userToUpdate;
    const startTime = performance.now(); 
    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/update_role`,
        "PUT",
        { membership_id: membershipId, new_role: newRoleIndex }
      );

      const responseTime = Math.round(performance.now() - startTime);
      if (response.ok) {
        const res = await response.json();
        setNotification({ type: "success", content: res.message });
        Mixpanel.settings('Member Role', 'Updated', {
          member_id: memberToUpdate ? memberToUpdate.id : 'unknown',
          old_value: memberToUpdate ? memberToUpdate.role : 'unknown',
          new_value: newRoleIndex,
          response_time_ms: responseTime,
          startup_slug: slug
      });
      } else {
        const { error } = await response.json();
        setNotification({ type: "failure", content: error });
        Mixpanel.error('Member Role Update Error', {
          error_message: error,
          error_type: 'API Error',
          http_status: response.status,
          component: 'ManageRequestToJoinTeam',
          user_action: 'Update Member Role',
          member_id: memberToUpdate ? memberToUpdate.id : 'unknown',
          response_time_ms: responseTime,
          startup_slug: slug
        });
        console.error(error);
      }
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error during role update:", error);
      Mixpanel.error('Member Role Update Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'ManageRequestToJoinTeam',
        user_action: 'Update Member Role',
        member_id: memberToUpdate ? memberToUpdate.id : 'unknown',
        response_time_ms: responseTime,
        startup_slug: slug
      });
    }
  };

  const removeMember = async (user_id, index, newOwnerId) => {
    const startTime = performance.now();
    try {
      // Build the payload: include new_owner_id if provided.
      const payload = newOwnerId
        ? { user_id, new_owner_id: newOwnerId }
        : { user_id };

      const response = await fetchApi(
        `/startup_entities/${slug}/remove_member`,
        "DELETE",
        payload
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (response.ok) {
        const res = await response.json();
        setNotification({ type: "success", content: res.message });
        console.log(res);
        removeMembers(user_id || res.user_id);
        if (index !== undefined) {
          setUsers((prev) => {
            const newRequests = [
              ...prev.not_pending_requests.slice(0, index),
              ...prev.not_pending_requests.slice(index + 1),
            ];
            return { ...prev, not_pending_requests: newRequests };
          });
        }
        Mixpanel.content('Team Member', 'Removed', {
          removed_member_id: userIdToRemove,
          is_owner_transfer: !!newOwnerId,
          new_owner_id: newOwnerId || null,
          response_time_ms: responseTime,
          startup_slug: slug
      });
      } else {
        const { error } = await response.json();
        setNotification({ type: "failure", content: error });
        Mixpanel.error('Member Removal Error', {
          error_message: error,
          error_type: 'API Error',
          http_status: response.status,
          component: 'ManageRequestToJoinTeam',
          user_action: newOwnerId ? 'Transfer Ownership and Leave' : 'Remove Member',
          removed_member_id: userIdToRemove,
          response_time_ms: responseTime,
          startup_slug: slug
        });
        console.error(error);
      }
    } catch (error) {
      Mixpanel.error('Member Removal Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'ManageRequestToJoinTeam',
        user_action: newOwnerId ? 'Transfer Ownership and Leave' : 'Remove Member',
        removed_member_id: userIdToRemove,
        response_time_ms: responseTime,
        startup_slug: slug
      });
      console.error("Error during removal:", error);

    }
  };

  const openRemoveMemberModal = (user, index) => {
    setRemoveMemberModal({ opened: true, user, index });
  };

  const closeRemoveMemberModal = () => {
    setRemoveMemberModal({ opened: false, user: null, index: undefined });
  };

  return (
    <>
      {opened && (
        <Modal
          opened={opened}
          size="xl"
          onClose={() => {
            setNotification(null);
            close();
          }}
          withCloseButton={false}
        >
          {notification && <Notification {...notification} />}
          <Card radius="md">
            <Card.Section withBorder inheritPadding py="xs">
              <Text fw={500}>{t("startups_page.manage_team.pending")}</Text>
            </Card.Section>
            <div className="flow-root">
              <ul
                role="list"
                className="divide-y divide-gray-200 dark:divide-gray-700 list-none"
              >
                {users.pending_requests.map(({ user }, i) => (
                  <li className="py-3 sm:py-4" key={user.email}>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                       
                        {user.avatar_url ? (
                          <Avatar
                          src={
                            user.avatar_url 
                          }
                          mx="auto"
                          className="w-8 h-8 rounded-full"
                          
                        /> 
                        ): (
                          <Avatar
                          mx="auto"
                          className="w-8 h-8 rounded-full" 
                        >{`${user.first_name[0]}${user.last_name[0]}`}</Avatar> 
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <a href={`/profile/${user.id}`}>
                          <p className="text-sm font-medium text-gray-900 truncate dark:text-white">
                            {`${user.first_name} ${user.last_name}`}
                          </p>
                          <p className="text-sm text-gray-500 truncate dark:text-gray-400">
                            {user.email}
                          </p>
                        </a>
                      </div>
                      <div className="inline-flex items-center text-base font-semibold text-gray-900 dark:text-white">
                        <Group gap={0} justify="flex-end">
                          <ActionIcon
                            variant="default"
                            size="xl"
                            onClick={() => handleRequest(true, i)}
                          >
                            <IconCheck
                              style={{ width: rem(22), height: rem(22) }}
                              stroke={1.5}
                            />
                          </ActionIcon>
                          <ActionIcon
                            variant="default"
                            size="xl"
                            onClick={() => handleRequest(false, i)}
                          >
                            <IconX
                              style={{ width: rem(22), height: rem(22) }}
                              color="red"
                              stroke={1.5}
                            />
                          </ActionIcon>
                        </Group>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <Card.Section withBorder inheritPadding pt={25} pb={10}>
              <Text fw={500}>{t("startups_page.manage_team.all")}</Text>
            </Card.Section>
            <div className="flow-root">
              <ul
                role="list"
                className="divide-y divide-gray-200 dark:divide-gray-700 list-none"
              >
                {users.not_pending_requests.map(({ user }, i) => (
                  <li className="py-3 sm:py-4" key={user.email}>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                      {user.avatar_url ? (
                          <Avatar
                          src={
                            user.avatar_url 
                          }
                          mx="auto"
                          className="w-8 h-8 rounded-full"
                          
                        /> 
                        ): (
                          <Avatar
                          mx="auto"
                          className="w-8 h-8 rounded-full" 
                        >{`${user.first_name[0]}${user.last_name[0]}`}</Avatar> 
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <a href={`/profile/user/${user.id}`}>
                          <p className="text-sm font-medium text-gray-900 truncate dark:text-white">
                            {`${user.first_name} ${user.last_name}`}
                          </p>
                          <p className="text-sm text-gray-500 truncate dark:text-gray-400">
                            {user.email}
                          </p>
                        </a>
                      </div>
                      <div className="inline-flex items-center text-base font-semibold text-gray-900 dark:text-white">
                        <Group gap={0} justify="flex-end">
                          <CustomDropdown
                            id={user.membership_id}
                            handleRole={handleRole}
                            role={user.role}
                            userToUpdate={user}
                          />
                          {user.id !== id && (
                            <ActionIcon
                              variant="default"
                              size="xl"
                              onClick={() => openRemoveMemberModal(user, i)}
                            >
                              <IconTrash
                                style={{ width: rem(22), height: rem(22) }}
                                color="red"
                                stroke={1.5}
                              />
                            </ActionIcon>
                          )}
                        </Group>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </Modal>
      )}

      {/* Remove Member Confirmation Modal */}
      {removeMemberModal.opened && (
        <Modal
          opened={removeMemberModal.opened}
          onClose={closeRemoveMemberModal}
          size="lg"
          withCloseButton={false}
        >
          <div className="p-4 overflow-y-auto">
            <Text fw={500}>
              Are you sure you want to remove{" "}
              {removeMemberModal.user &&
                `${removeMemberModal.user.first_name} ${removeMemberModal.user.last_name}`}{" "}
              from the team?
            </Text>
          </div>
          <div className="flex justify-end items-center gap-x-2 py-3 px-4">
            <Button
              onClick={closeRemoveMemberModal}
              variant="default"
              className="px-12"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                removeMember(
                  removeMemberModal.user.id,
                  removeMemberModal.index
                );
                closeRemoveMemberModal();
              }}
              variant="default"
              className="text-red-700 px-12"
            >
              Proceed anyway
            </Button>
          </div>
        </Modal>
      )}

      {/* Render LeaveStartup for both member and owner */}
      {(isMember || isOwner) && (
        <LeaveStartup
          removeMember={removeMember}
          entity={entity}
          isOwner={isOwner}
          members={members}
          slug={slug}
        />
      )}

      {!isOwner && (isMember || role < 1) ? null : (
        <Indicator
          withBorder
          disabled={!pendingJoinRequest}
          processing
          color="green"
          position="top-start"
          size={15}
        >
          <UnstyledButton onClick={fetchData}>
            <Group>
              <ActionIcon variant="subtle">
                <IconUsersGroup size={24} />
              </ActionIcon>
              <p style={{ fontSize: "16px" }}>
                {t("startups_page.members.manage_team")}
              </p>
            </Group>
          </UnstyledButton>
        </Indicator>
      )}
    </>
  );
}

const LeaveStartup = ({ removeMember, entity, isOwner, members, slug }) => {
  const [opened, { close, open }] = useDisclosure(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState("");
  const [localNotification, setLocalNotification] = useState(null);

  // Access the current user's id to filter out from the dropdown options
  const {
    user: { id: currentUserId },
  } = useUserStore((state) => state.user);

  // Filter out the current owner from the dropdown options.
  const options = members
    .filter((member) => member.id !== currentUserId)
    .map((member) => ({
      value: String(member.id),
      label: `${member.first_name} ${member.last_name} (${member.email})`,
    }));

  return (
    <>
      {opened && (
        <Modal
          opened={opened}
          onClose={close}
          size="lg"
          withCloseButton={false}
        >
          <div className="p-4 overflow-y-auto">
            {localNotification && <Notification {...localNotification} />}
            {isOwner ? (
              <>
                <Text fw={500}>
                  You are about to leave this {entity.toLowerCase()} as the
                  owner.
                </Text>
                <Text mt="md">
                  To leave, please select a member to transfer ownership to:
                </Text>
                <Select
                  mt="sm"
                  placeholder="Select member"
                  data={options}
                  value={selectedNewOwner}
                  onChange={setSelectedNewOwner}
                />
              </>
            ) : (
              <Text className="mt-1 text-gray-800">
                You are about to leave this {entity.toLowerCase()}. You will
                lose all member access.
              </Text>
            )}
          </div>
          <div className="flex justify-end items-center gap-x-2 py-3 px-4">
            <Button onClick={close} variant="default" className="px-12">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (isOwner) {
                  // Pass the new owner id as the third parameter.
                  removeMember(null, undefined, selectedNewOwner);
                } else {
                  removeMember(null, undefined);
                }
                close();
              }}
              variant="default"
              className="text-red-700 px-12"
              disabled={isOwner && !selectedNewOwner}
            >
              Proceed anyway
            </Button>
          </div>
        </Modal>
      )}
      <UnstyledButton mb={10} onClick={open}>
        <Group>
          <ActionIcon variant="subtle">
            <IconDoor size={24} />
          </ActionIcon>
          <p style={{ fontSize: "16px" }}>Leave Project</p>
        </Group>
      </UnstyledButton>
    </>
  );
};

const CustomDropdown = ({ id, handleRole, role, userToUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(role);

  const roles = [
    "Founding Team",
    "Member",
    "Intern/Volunteer",
    "Consultant",
    "Investor",
    "Professor"
  ];

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleRoleChange = (index) => {
    setSelectedRoleIndex(index);
    setIsOpen(false);
    handleRole(id, index, userToUpdate);
  };

  return (
    <div className="relative inline-block text-left">
      <Menu
        opened={isOpen}
        onChange={toggleDropdown}
        shadow="md"
        width={200}
        withinPortal={true}
      >
        <Menu.Target>
          <Button variant="default">
            {roles[selectedRoleIndex] || role}
            <svg
              className={`w-2.5 h-2.5 ms-3 ${isOpen ? "rotate-180" : ""}`}
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 4 4 4-4"
              />
            </svg>
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <ul className="py-3">
            {roles.map((role, index) => (
              <li
                key={index}
                className="relative flex items-center p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <input
                  id={`role-${index}`}
                  type="radio"
                  value={index}
                  name="team-role"
                  checked={selectedRoleIndex === index}
                  onChange={() => handleRoleChange(index)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-700 dark:focus:ring-offset-gray-700 focus:ring-2 dark:bg-gray-600 dark:border-gray-500"
                />
                <label
                  htmlFor={`role-${index}`}
                  className="w-full ms-2 text-sm font-medium text-gray-900 rounded dark:text-gray-300"
                >
                  {role}
                </label>
              </li>
            ))}
          </ul>
        </Menu.Dropdown>
      </Menu>
    </div>
  );
};
